// File: frontend/src/pages/Socrates/components/AIAgentsAdmin/AgentVariables/index.js

import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faDatabase, faPlus, faSearch, 
  faEdit, faTrash, faEye, faEyeSlash 
} from '@fortawesome/free-solid-svg-icons';
import { useLanguage } from '../../../../../contexts/LanguageContext';
import SocratesCard from '../../common/SocratesCard';
import styles from './AgentVariables.module.css';

const AgentVariables = ({ agent }) => {
  const { t } = useLanguage();
  const [searchTerm, setSearchTerm] = useState('');
  const [showSecrets, setShowSecrets] = useState({});

  // Mock variables data
  const mockVariables = [
    {
      id: 'var_1',
      name: 'API_ENDPOINT',
      value: 'https://api.example.com/v1',
      type: 'string',
      isSecret: false,
      description: 'Primary API endpoint for external service calls',
      lastModified: '2024-01-20T14:30:00Z'
    },
    {
      id: 'var_2',
      name: 'DATABASE_URL',
      value: 'postgresql://user:****@localhost:5432/db',
      type: 'string',
      isSecret: true,
      description: 'Database connection string',
      lastModified: '2024-01-19T10:15:00Z'
    },
    {
      id: 'var_3',
      name: 'MAX_RETRIES',
      value: '3',
      type: 'number',
      isSecret: false,
      description: 'Maximum number of retry attempts',
      lastModified: '2024-01-18T16:45:00Z'
    },
    {
      id: 'var_4',
      name: 'API_KEY',
      value: 'sk-****************************',
      type: 'string',
      isSecret: true,
      description: 'Secret API key for authentication',
      lastModified: '2024-01-17T09:20:00Z'
    }
  ];

  const filteredVariables = mockVariables.filter(variable =>
    variable.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    variable.description.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const toggleSecretVisibility = (variableId) => {
    setShowSecrets(prev => ({
      ...prev,
      [variableId]: !prev[variableId]
    }));
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  const getDisplayValue = (variable) => {
    if (variable.isSecret && !showSecrets[variable.id]) {
      return '••••••••••••••••';
    }
    return variable.value;
  };

  return (
    <div className={styles.agentVariables}>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <h2 className={styles.title}>
            <FontAwesomeIcon icon={faDatabase} className={styles.titleIcon} />
            {t('variables')}
          </h2>
          <p className={styles.subtitle}>
            {t('manage_variables_for', { agentName: agent.display_name })}
          </p>
        </div>
        <div className={styles.headerRight}>
          <button className={styles.createButton}>
            <FontAwesomeIcon icon={faPlus} />
            <span>{t('add_variable')}</span>
          </button>
        </div>
      </div>

      <div className={styles.controls}>
        <div className={styles.searchContainer}>
          <FontAwesomeIcon icon={faSearch} className={styles.searchIcon} />
          <input
            type="text"
            placeholder={t('search_variables')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className={styles.searchInput}
          />
        </div>
      </div>

      <SocratesCard className={styles.variablesCard}>
        <div className={styles.cardContent}>
          {filteredVariables.length === 0 ? (
            <div className={styles.emptyState}>
              <FontAwesomeIcon icon={faDatabase} className={styles.emptyIcon} />
              <h3>{t('no_variables_found')}</h3>
              <p>{t('no_variables_description')}</p>
              <button className={styles.createFirstButton}>
                <FontAwesomeIcon icon={faPlus} />
                {t('create_first_variable')}
              </button>
            </div>
          ) : (
            <div className={styles.variablesList}>
              {filteredVariables.map((variable) => (
                <div key={variable.id} className={styles.variableItem}>
                  <div className={styles.variableInfo}>
                    <div className={styles.variableHeader}>
                      <h4 className={styles.variableName}>{variable.name}</h4>
                      <div className={styles.variableBadges}>
                        <span className={`${styles.typeBadge} ${styles[variable.type]}`}>
                          {variable.type}
                        </span>
                        {variable.isSecret && (
                          <span className={styles.secretBadge}>
                            {t('secret')}
                          </span>
                        )}
                      </div>
                    </div>
                    <p className={styles.variableDescription}>
                      {variable.description}
                    </p>
                    <div className={styles.variableValue}>
                      <span className={styles.valueLabel}>{t('value')}:</span>
                      <code className={styles.valueCode}>
                        {getDisplayValue(variable)}
                      </code>
                      {variable.isSecret && (
                        <button
                          className={styles.toggleVisibility}
                          onClick={() => toggleSecretVisibility(variable.id)}
                          title={showSecrets[variable.id] ? t('hide_value') : t('show_value')}
                        >
                          <FontAwesomeIcon 
                            icon={showSecrets[variable.id] ? faEyeSlash : faEye} 
                          />
                        </button>
                      )}
                    </div>
                    <div className={styles.variableMeta}>
                      <span className={styles.metaItem}>
                        {t('modified')}: {formatDate(variable.lastModified)}
                      </span>
                    </div>
                  </div>
                  <div className={styles.variableActions}>
                    <button className={styles.actionButton} title={t('edit')}>
                      <FontAwesomeIcon icon={faEdit} />
                    </button>
                    <button className={styles.actionButton} title={t('delete')}>
                      <FontAwesomeIcon icon={faTrash} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </SocratesCard>
    </div>
  );
};

export default AgentVariables;